
import React, { useEffect, useState } from "react";
import { Card, Table, Pagination, Form, InputGroup, Button } from "react-bootstrap";
import { IconSearch, IconChevronUp, IconChevronDown } from "@tabler/icons-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { Helmet } from "react-helmet-async";

const CreditsHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  useEffect(() => {
    fetchTransactions();
  }, [page, limit, searchQuery, sortBy, sortOrder]);

  const getPaginationPages = (current, total) => {
    const delta = 2;
    const pages = new Set();
    pages.add(1);
    pages.add(total);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
        search: searchQuery,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      const response = await fetchWithAuth(`/api/credit-history-admin?${params}`);
      const data = await response.json();

      setTransactions(data.results || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Ошибка при загрузке транзакций:", error);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const renderSortHeader = (label, key, className = "") => (
    <th
      onClick={() => {
        if (sortBy === key) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(key);
          setSortOrder("asc");
        }
      }}
      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
      className={className}
    >
      {label}{" "}
      {sortBy === key &&
        (sortOrder === "asc" ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />)}
    </th>
  );

  // --- Utils for Snippet Info formatting ---
  const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const parseFromHtml = (html) => {
    if (!html) return { title: "", description: "" };
    try {
      const container = document.createElement("div");
      container.innerHTML = html;

      const root = container.firstElementChild && container.firstElementChild.tagName === "DIV"
        ? container.firstElementChild
        : container;

      const children = Array.from(root.children).filter((el) => el.tagName === "DIV");
      let title = children[0] ? (children[0].textContent || "").trim() : "";
      let description = children[1] ? (children[1].textContent || "").trim() : "";

      if (!description && title && title.length > 120) {
        const m = title.match(/(.+?[.!?])\s+(.*)$/);
        if (m) {
          title = m[1].trim();
          description = m[2].trim();
        }
      }

      if (description && title.includes(description)) {
        title = title.replace(description, "").trim();
      }

      return { title, description };
    } catch {
      const text = stripHtml(html);
      const m = text.match(/^(.*?)(?:[.!?])\s+(.*)$/);
      if (m) {
        return { title: m[1].trim(), description: m[2].trim() };
      }
      return { title: text.trim(), description: "" };
    }
  };

  const parseSnippetInfo = (tx) => {
    let title = tx.snippet_title || "";
    let description = tx.snippet_description || "";

    if (!title && !description && tx.snippet_info) {
      const parsed = parseFromHtml(tx.snippet_info);
      title = parsed.title;
      description = parsed.description;
    }

    return { title, description };
  };

  return (<><Helmet> <title>Credit History Admin - IndexBooster</title></Helmet>
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h3 className="m-0">Credits History</h3>
        <InputGroup style={{ maxWidth: 300 }}>
          <InputGroup.Text>
            <IconSearch size={18} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search URL"
            value={searchQuery}
            onChange={(e) => {
              setPage(1);
              setSearchQuery(e.target.value);
            }}
          />
        </InputGroup>
      </Card.Header>
      <div className="table-responsive">
        <Table className="table card-table table-vcenter text-nowrap datatable" hover responsive>
          <thead>
            <tr>
              <th></th>
              {renderSortHeader("URL", "url")}
              {renderSortHeader("User", "user", "text-center")}
              {renderSortHeader("Sum", "amount", "text-center")}
              {renderSortHeader("Type", "transaction_type", "text-center")}
              {renderSortHeader("Date", "created_at", "text-center")}
              {renderSortHeader("Project", "project_name", "text-center")}
              <th>Snippet Info</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-4">
                  No Data
                </td>
              </tr>
            ) : (
              transactions.map((tx) => {
                const { title, description } = parseSnippetInfo(tx);

                return (
                  <tr key={tx.id}>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={selectedTransactions.includes(tx.id)}
                        onChange={() => {
                          setSelectedTransactions((prev) =>
                            prev.includes(tx.id)
                              ? prev.filter((id) => id !== tx.id)
                              : [...prev, tx.id]
                          );
                        }}
                      />
                    </td>

                    {/* URL: adaptive width with wrapping */}
                    <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      <a href={tx.url} target="_blank" rel="noopener noreferrer">
                        {tx.url}
                      </a>
                    </td>

                    <td className="text-center">
                      {tx.user_id ? (
                        <a href={`/user/${tx.user_id}/projects`} target="_blank" rel="noopener noreferrer">
                          {tx.user}
                        </a>
                      ) : (
                        tx.user
                      )}
                    </td>

                    <td className="text-center">{tx.amount > 0 ? `+${tx.amount}` : tx.amount}</td>
                    <td className="text-center">{tx.transaction_type}</td>

                    <td className="text-center">
                      {new Date(tx.created_at).toLocaleDateString()}
                      <br />
                      {new Date(tx.created_at).toLocaleTimeString()}
                    </td>

                    <td className="text-center">
                      {tx.project_id ? (
                        <a href={`/project/${tx.project_id}`} target="_blank" rel="noopener noreferrer">
                          {tx.project_name}
                        </a>
                      ) : (
                        tx.project_name
                      )}
                    </td>

                    {/* Snippet Info styled like ProjectView */}
                    <td style={{ maxWidth: "420px", whiteSpace: "normal", wordBreak: "break-word", textAlign: "left" }}>
                      {title || description ? (
                        <div>
                          {title ? <div style={{ color: "#1a0dab", fontWeight: 500 }}>{title}</div> : null}
                          {description ? <div style={{ color: "#000" }}>{description}</div> : null}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center px-4">
        <div className="d-flex align-items-center gap-3 mb-2 mb-md-0">
          <Button
            variant="danger"
            disabled={selectedTransactions.length === 0}
            onClick={async () => {
              const confirmed = window.confirm(
                `Are you sure you want to delete ${selectedTransactions.length} record${selectedTransactions.length > 1 ? "s" : ""}?`
              );
              if (!confirmed) return;

              try {
                await fetchWithAuth(
                  `/api/credit-history-admin/delete?${selectedTransactions
                    .map((id) => `ids=${id}`)
                    .join("&")}`,
                  { method: "DELETE" }
                );
                setSelectedTransactions([]);
                fetchTransactions();
              } catch (error) {
                console.error("Error deleting:", error);
              }
            }}
          >
            Remove
          </Button>

          <Pagination className="mb-0">
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)}>
              «
            </Pagination.Prev>
            {getPaginationPages(page, totalPages).map((p, i) =>
              p === "..." ? (
                <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
              ) : (
                <Pagination.Item key={`page-${p}`} active={p === page} onClick={() => setPage(p)}>
                  {p}
                </Pagination.Item>
              )
            )}
            <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)}>
              »
            </Pagination.Next>
          </Pagination>

          <div className="text-muted">
            Showing <b>{(page - 1) * limit + 1}</b>–<b>{Math.min(page * limit, total)}</b> of <b>{total}</b> entries
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="text-muted">Show</span>
          <Form.Select
            value={limit}
            onChange={(e) => {
              setPage(1);
              setLimit(parseInt(e.target.value));
            }}
            size="sm"
          >
            {[25, 50, 100, 200, 500, 1000].map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </Form.Select>
          <span className="text-muted">entries</span>
        </div>
      </Card.Footer>
    </Card>
  </>);
};

export default CreditsHistory;
